import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import {
  IWebsite,
  IWebsiteDisplaySettingResponse,
  IWebsiteResponse,
  IWebsitesResponse
} from "../models/Website";
import { useEffect, useState } from "react";

// Custom hook for fetching a single website by ID
export function useWebsite(id: string) {
  const { get } = useApiHelper();
  return useQuery<IWebsiteResponse>({
    queryKey: ["websites", id],
    queryFn: () => get<IWebsiteResponse>(`/website/${id}`),
    enabled: !!id,
    staleTime: 1000 * 60, // 1 minute
  });
}

export default function useWebsites() {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const [websites, setWebsites] = useState<IWebsite[]>([]);

  // Fetch all websites for the authenticated customer
  const websitesQuery = useQuery<IWebsitesResponse>({
    queryKey: ["websites"],
    queryFn: () => get<IWebsitesResponse>("/website"),
    staleTime: 1000 * 60, // 1 minute
  });

  useEffect(() => {
    if (!websitesQuery.isLoading && websitesQuery.data) {
      setWebsites(websitesQuery.data.data);
    }
  }, [websitesQuery.isLoading, websitesQuery.data]);

  const createWebsite = useMutation(
    (website: any) => post<IWebsite>('/website', website),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["websites"]);
      },
    }
  );

  const updateWebsite = useMutation(
    (website: any) => put<IWebsite>(`/website/${website.id}`, website),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["websites"]);
        queryClient.invalidateQueries(["websites", variables.id]);
      },
    }
  );

  const deleteWebsite = useMutation(
    (id: string) => del(`/website/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["websites"]);
      },
    }
  );

  const getDisplaySetting = async (websiteId: string) => {
    const response = await get<IWebsiteDisplaySettingResponse>(`/website/${websiteId}/display-settings`);
    return response.data;
  };

  const upsertDisplaySetting = useMutation(
    ({
      websiteId,
      iframeHeight,
      zIndex,
      showTitle,
      wrapPageInContent,
      actionButtonPosition,
      hidePreferredCollection,
      hideSteps,
      preferredCollectionMinDays,
      preferredCollectionMaxDays,
      allowWeekends,
      depositEnabled,
      depositThreshold,
      depositType,
      depositPercent,
      depositFixed,
    }: {
      websiteId: string;
      iframeHeight: number | null;
      zIndex: number | null;
      showTitle: boolean;
      wrapPageInContent: boolean;
      actionButtonPosition: string;
      hidePreferredCollection: boolean;
      hideSteps: boolean;
      preferredCollectionMinDays: number | null;
      preferredCollectionMaxDays: number | null;
      allowWeekends: boolean;
      depositEnabled?: boolean;
      depositThreshold?: number;
      depositType?: string;
      depositPercent?: number;
      depositFixed?: number;
    }) =>
      put<{
        iframeHeight: number | null;
        zIndex: number | null;
        showTitle: boolean;
        wrapPageInContent: boolean;
        actionButtonPosition: string;
        hidePreferredCollection: boolean;
        hideSteps: boolean;
        preferredCollectionMinDays: number | null;
        preferredCollectionMaxDays: number | null;
        allowWeekends: boolean;
        depositEnabled?: boolean;
        depositThreshold?: number;
        depositType?: string;
        depositPercent?: number;
        depositFixed?: number;
      }>(`/website/${websiteId}/display-settings`, {
        iframeHeight,
        zIndex,
        showTitle,
        wrapPageInContent,
        actionButtonPosition,
        hidePreferredCollection,
        hideSteps,
        preferredCollectionMinDays,
        preferredCollectionMaxDays,
        allowWeekends,
        ...(depositEnabled === undefined
          ? {}
          : { depositEnabled, depositThreshold, depositType, depositPercent, depositFixed }),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["websites"]);
      },
    }
  );

  return {
    websitesQuery,
    websites,
    createWebsite,
    updateWebsite,
    deleteWebsite,
    getDisplaySetting,
    upsertDisplaySetting,
  };
}
